import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { 
  Receipt, 
  Search, 
  Filter, 
  Eye, 
  Download, 
  Send, 
  Building2,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { DebitNote } from '@/data/mockData';

interface DebitNotesListProps {
  debitNotes: DebitNote[];
  onView: (debitNote: DebitNote) => void;
  onSendToVendor: (debitNote: DebitNote) => void;
  onDownload: (debitNote: DebitNote) => void;
  onUpdateStatus: (debitNote: DebitNote, status: DebitNote['status']) => void;
}

export const DebitNotesList: React.FC<DebitNotesListProps> = ({
  debitNotes,
  onView,
  onSendToVendor,
  onDownload,
  onUpdateStatus
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vendorFilter, setVendorFilter] = useState<string>('all');

  // Get unique vendors for filter
  const vendors = Array.from(new Set(debitNotes.map(dn => dn.vendorName)));

  // Filter debit notes
  const filteredDebitNotes = debitNotes.filter(debitNote => {
    const matchesSearch = 
      debitNote.debitNoteNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debitNote.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debitNote.linkedPONumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debitNote.linkedInvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || debitNote.status === statusFilter;
    const matchesVendor = vendorFilter === 'all' || debitNote.vendorName === vendorFilter;

    return matchesSearch && matchesStatus && matchesVendor;
  });

  const getStatusVariant = (status: DebitNote['status']) => {
    switch (status) {
      case 'Open':
        return 'secondary';
      case 'Sent to Vendor':
        return 'default';
      case 'Closed':
        return 'outline';
      case 'Disputed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: DebitNote['status']) => {
    switch (status) {
      case 'Open':
        return <Clock className="h-4 w-4" />;
      case 'Sent to Vendor':
        return <Send className="h-4 w-4" />;
      case 'Closed':
        return <CheckCircle className="h-4 w-4" />;
      case 'Disputed':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: DebitNote['status']) => {
    switch (status) {
      case 'Open':
        return 'text-yellow-600';
      case 'Sent to Vendor':
        return 'text-blue-600';
      case 'Closed':
        return 'text-green-600';
      case 'Disputed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search debit notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Sent to Vendor">Sent to Vendor</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                  <SelectItem value="Disputed">Disputed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Vendor</label>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.map(vendor => (
                    <SelectItem key={vendor} value={vendor}>
                      {vendor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setVendorFilter('all');
                }}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Debit Notes</p>
                <p className="text-2xl font-bold">{debitNotes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Open</p>
                <p className="text-2xl font-bold">
                  {debitNotes.filter(dn => dn.status === 'Open').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sent to Vendor</p>
                <p className="text-2xl font-bold">
                  {debitNotes.filter(dn => dn.status === 'Sent to Vendor').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Closed</p>
                <p className="text-2xl font-bold">
                  {debitNotes.filter(dn => dn.status === 'Closed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Debit Notes Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Debit Notes</CardTitle>
          <CardDescription>
            {filteredDebitNotes.length} debit notes found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Debit Note #</TableHead>
                  <TableHead>Linked Document</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDebitNotes.map((debitNote) => (
                  <TableRow key={debitNote.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{debitNote.debitNoteNumber}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          {debitNote.linkedPONumber && (
                            <div>
                              <Badge variant="outline" className="text-xs">
                                PO: {debitNote.linkedPONumber}
                              </Badge>
                            </div>
                          )}
                          {debitNote.linkedInvoiceNumber && (
                            <div>
                              <Badge variant="outline" className="text-xs">
                                INV: {debitNote.linkedInvoiceNumber}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{debitNote.vendorName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{debitNote.reason}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-red-600">
                        {formatCurrency(debitNote.totalAmount)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(debitNote.status)} className="flex items-center gap-1 w-fit">
                        {getStatusIcon(debitNote.status)}
                        {debitNote.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {new Date(debitNote.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onView(debitNote)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {debitNote.status === 'Open' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onSendToVendor(debitNote)}
                            title="Send to Vendor"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDownload(debitNote)}
                          title="Download PDF"
                          className="text-green-600 hover:text-green-700"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredDebitNotes.length === 0 && (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No debit notes found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' || vendorFilter !== 'all'
                  ? 'Try adjusting your filters to see more results.'
                  : 'No debit notes have been created yet.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
